import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  ReviewSessionCard,
  ReviewSessionCardSkeleton,
} from '@/components/review/ReviewSessionCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types/auth';
import { api } from '@/lib/api';
import type { ReviewSessionSummary } from '@/types/api';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_review', label: 'In Review' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'approved', label: 'Approved' },
  { value: 'revision_requested', label: 'Revision Requested' },
] as const;

const URGENCY_OPTIONS = [
  { value: 'all', label: 'All Urgency' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;

function urgencyLevel(score: number): string {
  if (score >= 9) return 'critical';
  if (score >= 7) return 'high';
  if (score >= 5) return 'medium';
  return 'low';
}

export function ReviewQueuePage() {
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const isDepartmentRep = user?.role === UserRole.DEPARTMENT_REP;

  const [selectedStatus, setSelectedStatus] = useState(
    searchParams.get('status') ?? 'all',
  );
  const [selectedDepartment, setSelectedDepartment] = useState(
    searchParams.get('department') ?? 'all',
  );
  const [selectedUrgency, setSelectedUrgency] = useState(
    searchParams.get('urgency') ?? 'all',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(20);

  const apiFilters: Record<string, string | number> = {};
  if (selectedStatus !== 'all') apiFilters.status = selectedStatus;
  if (selectedDepartment !== 'all') apiFilters.department = selectedDepartment;

  const {
    data: response,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['review-sessions', apiFilters],
    queryFn: () => api.reviewSessions.list(apiFilters),
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const sessions = (response as unknown as { sessions: ReviewSessionSummary[]; total: number })?.sessions ?? [];

  // Client-side filtering for urgency and search
  const filtered = useMemo(() => {
    let result = [...sessions];

    if (selectedUrgency !== 'all') {
      result = result.filter(
        (s) => urgencyLevel(s.urgencyScore) === selectedUrgency,
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((s) =>
        s.documentTitle.toLowerCase().includes(q),
      );
    }

    result.sort((a, b) => b.urgencyScore - a.urgencyScore);
    return result;
  }, [sessions, selectedUrgency, searchQuery]);

  const departments = useMemo(() => {
    const depts = new Set(sessions.map((s: ReviewSessionSummary) => s.department));
    return Array.from(depts).sort();
  }, [sessions]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: sessions.length };
    for (const s of sessions) {
      counts[s.status] = (counts[s.status] ?? 0) + 1;
    }
    return counts;
  }, [sessions]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  if (!user || isLoading) {
    return (
      <div className="space-y-4 p-6">
        {Array.from({ length: 5 }, (_, i) => (
          <ReviewSessionCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Review Queue"
        subtitle="Documents awaiting your review"
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={`mr-2 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
        }
      />

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          value={selectedStatus}
          onValueChange={(v) => setSelectedStatus(v as string)}
        >
          <TabsList variant="line">
            {STATUS_OPTIONS.map((opt) => (
              <TabsTrigger key={opt.value} value={opt.value}>
                {opt.label}
                {(statusCounts[opt.value] ?? 0) > 0 && (
                  <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">
                    {statusCounts[opt.value]}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex flex-1 items-center justify-end gap-3">
          {!isDepartmentRep && (
            <Select
              value={selectedDepartment}
              onValueChange={(v) => setSelectedDepartment(v ?? 'all')}
            >
              <SelectTrigger size="sm">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={selectedUrgency} onValueChange={(v) => setSelectedUrgency(v ?? 'all')}>
            <SelectTrigger size="sm">
              <SelectValue placeholder="All Urgency" />
            </SelectTrigger>
            <SelectContent>
              {URGENCY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative w-64">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by document title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-7 pl-8 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Session List */}
      <div className="space-y-3">
        {isLoading ? (
          Array.from({ length: 5 }, (_, i) => (
            <ReviewSessionCardSkeleton key={i} />
          ))
        ) : isError ? (
          <EmptyState
            icon={ClipboardCheck}
            title="Failed to load sessions"
            description="Something went wrong. Please try again."
            action={
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={ClipboardCheck}
            title="No sessions to review"
            description="You're all caught up."
          />
        ) : (
          <>
            {visible.map((session) => (
              <ReviewSessionCard key={session.sessionId} session={session} />
            ))}

            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                Showing {visible.length} of {filtered.length} sessions
              </p>
              {hasMore && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setVisibleCount((prev) => prev + 20)}
                >
                  Load More
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
